// sagas/authSaga.js
import { call, put, takeLatest, debounce, select, race, take, delay, actionChannel, flush, fork } from 'redux-saga/effects';
import { buffers } from 'redux-saga';
import {
  LOGIN_SUCCESS,
  LOGIN_REQUEST,
  LOGOUT_REQUEST,
  loginSuccess,
  loginFailure,
  logoutSuccess,
  REAUTHENTICATE_REQUEST,
  REFRESH_TOKEN_REQUEST,
  REGISTER_REQUEST,
  registerSuccess,
  registerFailure,
  clearTokens,
  storeUserDetails,
  RESET_PASSWORD_REQUEST,
  resetPasswordSuccess,
  resetPasswordFailure,
  SETNEW_PASSWORD_REQUEST,
  setNewPasswordSuccess,
  setNewPasswordFailure,
  SOCIAL_MEDIA_LOGIN_REQUEST,
  SOCIAL_MEDIA_LOGIN_CALLBACK,
  socialMediaLoginSuccess, socialMediaLoginFailure,
  REFRESH_TOKEN_SUCCESS,
  TWO_FA_REQUIRED,
  TWO_FA_VERIFY_SUCCESS

} from '../actions/authActions';
import {
  FETCH_PARTNER_REQUEST,
  fetchPartnerSuccess,
  fetchPartnerFailure,
  setPaginationMeta,
} from '../actions/actions'

import {
  loginApi, registerApi, logoutRequestAPI, sendPasswordLinkRequest, setNewPasswordRequest,
  socialMediaLoginRequestAPI, socialMediaCallbackAPI,
} from '../../api/authApi'
import { isTokenValid, refreshAccessToken } from '../../utils/tokenUtils';
import { fetchPartnersApi } from '../../api/usermanagmentApi';
import { decodePermission, decodePermissionNotTransformed } from '../../utils/generalUtils';


// Worker Saga: Handling login API call
function* handleLogin(action) {
  try {
    //  Clear persisted state (e.g., if logging in as a different user)
    const response = yield call(loginApi, action.payload);
    // ── 2FA required ──
    if (response['2fa_required']) {
      yield put({
        type: TWO_FA_REQUIRED,
        payload: {
          tempToken: response.temp_token,
          availableMethods: response.available_methods,
          totpConfigured: response.totp_configured,
          email: response.email,
        }
      });
      return;
    }
    const { access, refresh, user_id, first_name, last_name, email, profile_image_url, permissions, user_type, designation, timezone, preferred_language, session_minutes } = response;
    // const { accessToken, refreshToken } = response;
    if (response.access && response.refresh) {
      // Store access token and refresh token in localStorage
      // yield put({ type: LOGIN_SUCCESS, payload: { access, refresh } });
      yield put({ type: 'STORE_TOKENS', payload: { accessToken: access, refreshToken: refresh, session_minutes: session_minutes } });
      // yield put(storeTokens(access, refresh));
      localStorage.setItem('accessToken', access);
      localStorage.setItem('refreshToken', refresh);
      localStorage.setItem('isAuthenticated', true)
      localStorage.setItem('session_minutes', session_minutes)
      // Optionally store user information
      localStorage.setItem('userId', user_id);
      localStorage.setItem('firstName', first_name);
      localStorage.setItem('lastName', last_name);
      localStorage.setItem('email', email);
      localStorage.setItem('profile_image_url', profile_image_url);
      localStorage.setItem('permissions', permissions);
      localStorage.setItem('user_type', user_type);
      localStorage.setItem('timeZone', timezone);
      localStorage.setItem('preferred_language', preferred_language);
      localStorage.setItem('designation', designation);
      const permissionConfig = yield call(decodePermission, permissions)
      const permissionConfigNotTransformed = yield call(decodePermissionNotTransformed, permissions)
      yield put(loginSuccess('Login successful!'));
      // Dispatch user details to the Redux store
      yield put(storeUserDetails({ user_id, first_name, last_name, email, profile_image_url, user_type, designation, permissions: permissionConfig, userPermissions: permissionConfigNotTransformed }));
      // Optionally navigate to the dashboard after login
      yield put({
        type: LOGIN_SUCCESS, payload: {
          userDetails: {

            first_name: response.first_name,
            last_name: response.last_name,
            email: response.email,
            profile_image_url: response.profile_image_url,
            permissions: permissionConfig,
            user_type: user_type,
            designation: designation
          },
          access, refresh
        }
      });
      // You may need to use history.push('/dashboard') or a similar method
    } else {
      // Dispatch login failure if no tokens are found in the response
      //  yield put(loginFailure('Login failed. Please try again.'));
      yield put(loginFailure(response.errors[0].detail));
    }
    // Dispatch success action
    // yield put({ type: LOGIN_SUCCESS, payload: {
    //   userDetails: {
    //     first_name: response.first_name,
    //     last_name: response.last_name,
    //     email: response.email,
    //   },
    //   access, refresh} });
    // yield put(loginSuccess({
    //     accessToken: response.access,
    //     refreshToken: response.refresh,


    // }));
  } catch (error) {
    console.error("Login error:", error);

    yield put(
      loginFailure(
        error?.errors?.[0]?.detail ||
        error?.message ||
        "Unable to connect. Please try again."
      )
    );
  }
}

function* handleTwoFAVerifySuccess(action) {
  try {
    const { access, refresh } = action.payload;

    yield put({ type: 'STORE_TOKENS', payload: { accessToken: access, refreshToken: refresh } });

    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);
    localStorage.setItem('isAuthenticated', true);

    // Fetch user details using the new access token if needed
    // OR if verify-2fa-otp returns user details, pass them in payload
    const { user_id, first_name, last_name, email, profile_image_url,
      permissions, user_type, designation, timezone,
      preferred_language, session_minutes } = action.payload;

    localStorage.setItem('session_minutes', session_minutes);
    localStorage.setItem('userId', user_id);
    localStorage.setItem('firstName', first_name);
    localStorage.setItem('lastName', last_name);
    localStorage.setItem('email', email);
    localStorage.setItem('profile_image_url', profile_image_url);
    localStorage.setItem('permissions', permissions);
    localStorage.setItem('user_type', user_type);
    localStorage.setItem('timeZone', timezone);
    localStorage.setItem('preferred_language', preferred_language);
    localStorage.setItem('designation', designation);

    const permissionConfig = yield call(decodePermission, permissions);
    const permissionConfigNotTransformed = yield call(decodePermissionNotTransformed, permissions);

    yield put(loginSuccess('Login successful!'));
    yield put(storeUserDetails({
      user_id, first_name, last_name, email, profile_image_url,
      user_type, designation,
      permissions: permissionConfig,
      userPermissions: permissionConfigNotTransformed
    }));
    yield put({
      type: LOGIN_SUCCESS,
      payload: {
        userDetails: {
          first_name, last_name, email, profile_image_url,
          permissions: permissionConfig, user_type, designation
        },
        access, refresh
      }
    });
  } catch (error) {
    yield put(loginFailure(error.errors?.[0]?.detail || 'Verification failed.'));
  }
}

function* handleRefreshTask() {
  try {
    const { refreshToken } = yield select(state => state.auth);
    if (!refreshToken) return;

    const result = yield call(refreshAccessToken);
    if (result?.access) {
      yield put({
        type: REFRESH_TOKEN_SUCCESS,
        payload: { accessToken: result.access, refreshToken: result.refresh }
      });
    } else {
      throw new Error('Refresh token invalid');
    }
  } catch (error) {
    console.error("Failed to refresh token:", error.message);
    yield put(clearTokens());
    yield put({ type: LOGOUT_REQUEST });
  }
}
// API call for registration
// function registerApi(userData) {
//   return fetch('https://staging.runmybot.com/api/register/', {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/json',
//     },
//     body: JSON.stringify(userData),
//   }).then(response => response.json());
// }

// Worker saga
function* handleRegister(action) {
  try {
    const response = yield call(registerApi, action.payload);
    if (response.errors && response.errors[0] && response.errors[0].detail) {

      const errorMessage = response.errors[0].detail;
      yield put(registerFailure(errorMessage));  // Dispatch failure with the specific error message
    } else if (response.message) {
      yield put(registerSuccess(response.message));
    } else {
      yield put(registerFailure('Registration failed. Please try again.'));
    }
  } catch (error) {
    yield put(registerFailure(error.errors[0].detail))
    // yield put(registerFailure('An error occurred during registration.'));
  }
}
export function* authQueueWatcher() {
  const authChannel = yield actionChannel(
    [REFRESH_TOKEN_REQUEST, LOGOUT_REQUEST],
    buffers.expanding()
  );

  while (true) {
    const action = yield take(authChannel);

    if (action.type === REFRESH_TOKEN_REQUEST) {
      yield call(handleRefreshTask);
    }

    if (action.type === LOGOUT_REQUEST) {
      yield call(handleLogoutTask);
      yield flush(authChannel); // drain any stale buffered refresh actions
    }
  }
}
// Watcher saga
export function* watchRegister() {
  yield takeLatest(REGISTER_REQUEST, handleRegister);
}

// Watcher Saga: Watches for login and refresh token actions
export default function* authSaga() {
  yield takeLatest(LOGIN_REQUEST, handleLogin);
  yield takeLatest(TWO_FA_VERIFY_SUCCESS, handleTwoFAVerifySuccess);
}


// Worker saga: handles the logout process
function* handleLogoutTask() {
  try {
    const { refreshToken: latestRefresh, accessToken: latestAccess } = yield select(state => state.auth);
    if (latestRefresh) {
      yield call(logoutRequestAPI, latestRefresh, latestAccess);
    }
    yield put(clearTokens());
    yield put(logoutSuccess());
  } catch (error) {
    // Already invalid = already logged out = success
    yield put(clearTokens());
    yield put(logoutSuccess());
  }
}
export function* watchTokenExpiration() {
  while (true) {
    const { accessToken, refreshToken } = yield select(state => state.auth);
    if (accessToken && refreshToken) {
      try {
        const jwtPayload = JSON.parse(atob(accessToken.split('.')[1]));
        const expTime = jwtPayload.exp * 1000;
        const timeLeft = expTime - Date.now();

        if (timeLeft > 0) {
          const { tokensUpdated } = yield race({
            timeout: delay(timeLeft),
            tokensUpdated: take('STORE_TOKENS'),
          });
          if (tokensUpdated) continue;
        }

        const { accessToken: latestToken, refreshToken: currentRefreshToken } = yield select(state => state.auth);
        if (!currentRefreshToken) {
          yield take('STORE_TOKENS');
          continue;
        }

        try {
          const latestPayload = JSON.parse(atob(latestToken.split('.')[1]));
          const realTimeLeft = latestPayload.exp * 1000 - Date.now();
          if (realTimeLeft > 5000) continue;
        } catch (e) { /* fall through */ }

        yield put({ type: REFRESH_TOKEN_REQUEST });

        const { failed } = yield race({
          success: take(REFRESH_TOKEN_SUCCESS),
          failed: take(LOGOUT_REQUEST),
        });

        if (failed) {
          yield take('STORE_TOKENS');
        }

      } catch (error) {
        console.error("Error decoding accessToken:", error);
        yield delay(30 * 1000);
      }
    } else {
      yield take('STORE_TOKENS');
    }
  }
}
// Saga to check if the token is valid on app load
export function* handleReAuthenticate(action) {
  // try {
  const token = localStorage.getItem('accessToken');

  if (token) {
    const refresh = localStorage.getItem('refreshToken');
    const user_id = localStorage.getItem('userId');
    const first_name = localStorage.getItem('firstName');
    const last_name = localStorage.getItem('lastName');
    const email = localStorage.getItem('email');
    const profile_image_url = localStorage.getItem('email');
    const user_type = localStorage.getItem('user_type');
    yield put(storeUserDetails({ user_id, first_name, last_name, email, profile_image_url }));
    yield put({ type: 'STORE_TOKENS', payload: { accessToken: token, refreshToken: refresh } });
    localStorage.setItem('isAuthenticated', true)
    yield put({
      type: LOGIN_SUCCESS, payload: {
        userDetails: {

          first_name: first_name,
          last_name: last_name,
          email: email,
          profile_image_url: profile_image_url,
          // permissions: permissionConfig,
          user_type: user_type
        },
        token, refresh
      }
    });
    // Validate the token (you can also refresh it if needed)
    const isValid = yield call(isTokenValid, token);
    if (isValid) {
      // Token is valid, you can update the state
      yield put({ type: 'REAUTHENTICATE_SUCCESS', payload: token });
    } else {

      // Try refreshing the token
      const newToken = yield call(refreshAccessToken, token);
      if (newToken) {
        // Save the new token
        localStorage.setItem('authToken', newToken);
        yield put({ type: 'REAUTHENTICATE_SUCCESS', payload: newToken });
      } else {
        // No valid token, log out the user
        yield put({ type: 'REAUTHENTICATE_FAILED' });
      }
    }
  }
  // }  catch (error) {
  //   console.error('Error during re-authentication:', error);
  //   yield put({ type: LOGIN_FAILURE });
  // }
}

// Watcher saga for re-authentication on app initialization
export function* watchReAuthenticate() {
  yield takeLatest(REAUTHENTICATE_REQUEST, handleReAuthenticate);
}
// Worker saga
function* resetPasswordSaga(action) {
  try {
    const response = yield call(sendPasswordLinkRequest, action.payload.email);
    yield put(resetPasswordSuccess(response.message)); // on success
  } catch (error) {
    yield put(resetPasswordFailure(error.message)); // handle error
  }
}

// Watcher saga
export function* watchResetPassword() {
  yield takeLatest(RESET_PASSWORD_REQUEST, resetPasswordSaga);
}


// Saga worker function
function* handleSetNewPassword(action) {
  try {
    const response = yield call(setNewPasswordRequest, action.payload);

    yield put(setNewPasswordSuccess(response.message || 'Password reset successful!'));

  }
  catch (error) {
    yield put(setNewPasswordFailure(error.message || 'Password reset failed.'))
  }
}

// Watcher saga
export function* watchSetNewPassword() {
  yield takeLatest(SETNEW_PASSWORD_REQUEST, handleSetNewPassword);
}





// Worker Saga for Google Login
function* handleSocialMediaLogin({ payload }) {
  try {

    const userData = yield call(socialMediaLoginRequestAPI, payload);

    const updatedUserData = {
      ...userData,
      ...payload
    };
    // Check if `login_url` matches known provider login URLs
    if (
      typeof updatedUserData.login_url === 'string' &&
      /(https:\/\/accounts\.google\.com|https:\/\/www\.linkedin\.com|https:\/\/login\.microsoftonline\.com)/.test(updatedUserData.login_url)
    ) {
      // Redirect to the provider's authorization URL
      window.location.href = updatedUserData.login_url;
      return; // Stop execution as we're redirecting
    }

    // Handle success in case no redirect is needed
    yield put(socialMediaLoginSuccess(updatedUserData));

  } catch (error) {
    yield put(socialMediaLoginFailure(error.message)); // Dispatch failure action with error
  }
}


function* handleSocialMediaLoginCallback({ payload }) {
  try {
    // Call API to exchange code for user data
    const userData = yield call(socialMediaCallbackAPI, payload);
    const { user_id, first_name, last_name, email, profile_image_url, permissions, user_type, designation, timezone, preferred_language, session_minutes } = userData;
    // Store tokens in localStorage (optional)
    if (userData.access && userData.refresh) {
      yield put({ type: 'STORE_TOKENS', payload: { accessToken: userData.access, refreshToken: userData.refresh, session_minutes: session_minutes } });
      localStorage.setItem('accessToken', userData.access);
      localStorage.setItem('refreshToken', userData.refresh);
      localStorage.setItem('isAuthenticated', true)
      localStorage.setItem('userId', user_id);
      localStorage.setItem('firstName', first_name);
      localStorage.setItem('lastName', last_name);
      localStorage.setItem('email', email);
      localStorage.setItem('permissions', permissions);
      localStorage.setItem('user_type', user_type);
      localStorage.setItem('timeZone', timezone);
      localStorage.setItem('preferred_language', preferred_language);
      localStorage.setItem('profile_image_url', profile_image_url);
      localStorage.setItem('designation', designation);
      localStorage.setItem('session_minutes', session_minutes)

      // Decode the base64-encoded permissions string, same as normal login,
      // so Sidebar's permission-gated menu items actually receive permissions/userPermissions.
      const permissionConfig = yield call(decodePermission, permissions);
      const permissionConfigNotTransformed = yield call(decodePermissionNotTransformed, permissions);

      yield put(storeUserDetails({
        user_id, first_name, last_name, email, profile_image_url,
        user_type, designation,
        permissions: permissionConfig,
        userPermissions: permissionConfigNotTransformed
      }));
    }
    yield put(socialMediaLoginSuccess(userData));
  } catch (error) {
    yield put(socialMediaLoginFailure(error.message));
  }
}

// Watcher Saga
export function* watchSocialMediaLogin() {
  yield takeLatest(SOCIAL_MEDIA_LOGIN_REQUEST, handleSocialMediaLogin);
  yield debounce(500, SOCIAL_MEDIA_LOGIN_CALLBACK, handleSocialMediaLoginCallback);
}
// Worker Saga: Handle the action
function* handleChildAction(action) {
  // try {

  // Perform side effects (e.g., API call)
  // const response = yield call(apiFunction, action.payload);

  // Dispatch a success action with the response
  yield put({ type: 'CHILD_ACTION_SUCCESS', payload: action.payload });
  // } catch (error) {
  // Dispatch a failure action with the error
  //     yield put({ type: 'CHILD_ACTION_FAILURE', payload: error.message });
  // }
}
// Watcher Saga: Watch for the action
export function* watchChildAction() {
  yield takeLatest('CHILD_ACTION', handleChildAction);
}

function* fetchPartnersSaga(action) {
  const { accessToken, page, pageSize } = action.payload;

  try {
    const response = yield call(fetchPartnersApi, accessToken, page, pageSize);
    // console.log("$$$$$$$$$$$ response.data ",response.results)
    // Assuming the response structure includes `data` and `meta`
    yield put(fetchPartnerSuccess(response.results));
    yield put(setPaginationMeta(response.meta)); // Update pagination metadata
  } catch (error) {
    yield put(fetchPartnerFailure(error.message)); // Dispatch failure action
    console.error('Error fetching users:', error.message);
  }
}

export function* watchPartners() {
  yield takeLatest(FETCH_PARTNER_REQUEST, fetchPartnersSaga);
}